import { List, Record } from 'immutable';
import { getNameFunctionType } from 'app/services/localization';
import { IEntity, IEntityType, EntityFields, EntityAttributes, EntityAssociations } from 'app/types/entity';
import EnumClass from 'app/constants/enums/EnumClass';
import { walkSchema } from 'app/utils/schema';
import { isUnknownObject } from 'app/utils/object';

interface ILabelable {
  getLabel: getNameFunctionType;
  getDescription?: getNameFunctionType;
}

interface IInlineLabelable {
  getInlineLabel?: getNameFunctionType;
}

type TranslationForBase<T> = T extends null ? ILabelable :
  T extends IEntity ? ILabelable :
  T extends Record<any> ? ILabelable :
  T extends EnumClass<string, any> ? ILabelable :
  T extends string ? ILabelable & IInlineLabelable :
  T extends number ? ILabelable :
  T extends boolean ? ILabelable & Required<IInlineLabelable> :
  T extends Date ? ILabelable :
  never;

export type TranslationForType<T> = T extends IEntity ? ILabelable :
  T extends List<infer U> ? TranslationForBase<U> :
  T extends Array<infer U> ? TranslationForBase<U> :
  T extends Record<any> ? ILabelable :
  T extends EnumClass<string, any> ? ILabelable :
  TranslationForBase<T>;

type TranslationsForAttributes<T extends IEntity> = {
  [K in keyof EntityAttributes<T>]: TranslationForType<T['attributes'][K]>;
};

type TranslationsForAssociations<T extends IEntity> = {
  [K in keyof EntityAssociations<T>]: TranslationForType<T['associations'][K]>;
};

export type TranslationsForEntity<T extends IEntity> = TranslationsForAttributes<T> & TranslationsForAssociations<T>;

class TranslationMap {
  public static create = () => {
    return new TranslationMap();
  };

  private constructor(
    private map: WeakMap<any, any> = new WeakMap(),
    private isTestMode: boolean = false,
  ) {}

  /**
   * Set translations for an Entity.
   * @param key the entity type to set translations for
   * @param value the translation values
   * @throws if the same key is set twice outside of testing mode.
   *
   * NOTE: Setting a key to undefined is primarily done for cleanup after tests.
   * Setting undefined actually deletes the key.
   */
  public set = <T extends IEntity>(key: IEntityType<T>, value: Partial<TranslationsForEntity<T>>) => {
    if (this.map.has(key) && !this.isTestMode) {
      // Because the map is mutable and global, we don't allow overridding values.
      throw new Error(`Attempted to set a new value for ${key.typeName}`);
    }

    this.map.set(key, value);
  };

  public get = <T extends IEntity>(key: IEntityType<T>): Partial<TranslationsForEntity<T>> | undefined => {
    return this.map.get(key);
  };

  public delete = <T extends IEntity>(key: IEntityType<T>) => {
    if (!this.isTestMode) {
      throw new Error(`Attempted to delete a value when no in test mode ${key.typeName}`);
    }
    return this.map.delete(key);
  };

  /**
   * When testing, enter a mode where we do not throw an error when mutating the map.
   */
  public enterTestMode = () => {
    this.isTestMode = true;
  };

  /**
   * After testing, revert the map to a state where it may not override values.
   */
  public exitTestMode = () => {
    this.isTestMode = false;
  };
}

export const ALL_TRANSLATIONS = TranslationMap.create();

// Test helper for mocking out the translations for an entity.
// defineClass is a class factory, normally classes create a runtime variable and identifier in the type namespace.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const mockTranslations = <T extends IEntity>(schema: IEntityType<T>) =>
  walkSchema({
    schema,
    onField: ({ field }) => ({
      getLabel: () => field,
      getInlineLabel: () => '',
      getDescription: () => '',
    }),
  });

const renderedUnsetTranslation = <T extends IEntity>(
  type: IEntityType<T>,
  field: string,
): getNameFunctionType => () => {
  throw new Error(`No translation specified for ${type.typeName}.${field}`);
};

export const getFieldLabel = <T extends IEntity>(
  type: IEntityType<T>,
  field: keyof EntityFields<T>,
): getNameFunctionType => {
  const translationMap = ALL_TRANSLATIONS.get(type);
  if (translationMap === undefined) {
    return renderedUnsetTranslation(type, field);
  }

  const fieldTranslations = translationMap[field];
  if (fieldTranslations === undefined) {
    return renderedUnsetTranslation(type, field);
  }

  return fieldTranslations.getLabel;
};

const hasGetInlineLabel = (x: unknown): x is Required<IInlineLabelable> => {
  return isUnknownObject(x) && typeof x.getInlineLabel === 'function';
};

const hasGetDescription = (x: unknown): x is Required<ILabelable> => {
  return isUnknownObject(x) && typeof x.getDescription === 'function';
};

export const getFieldInlineLabel = <T extends IEntity>(
  type: IEntityType<T>,
  field: keyof EntityFields<T>,
): getNameFunctionType | undefined => {
  const typeTranslations = ALL_TRANSLATIONS.get(type);
  const fieldTranslations = typeTranslations?.[field];
  return hasGetInlineLabel(fieldTranslations) ? fieldTranslations.getInlineLabel : undefined;
};

export const getFieldDescription = <T extends IEntity>(
  type: IEntityType<T>,
  field: keyof EntityFields<T>,
): getNameFunctionType | undefined => {
  const translationMap = ALL_TRANSLATIONS.get(type);
  const fieldTranslations = translationMap?.[field];
  return hasGetDescription(fieldTranslations) ? fieldTranslations.getDescription : undefined;
};
